/**
 * @file Render module for PickupForm Component
 * @author Mike Chacon
 */

import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import moment from "moment";

// Rubix Components
import { Container, Col, Row, Progress } from "reactstrap";
import PickupBlockSearchByGoogleAddress from "./pickupBlock-search-by-google-address";
import PickupBlockSelect from "./pickupBlock-selector";
import LocationNew from "./donor-location-new";
// import LocationChoose from './donor-location-choose'
import DonorNew from "./donor-new";
import PickupBlockSearchByLocation from "./pickupBlocks-search-by-donor-location";
import ImageUpload from "./image-upload";
import InfoPage from "./info-page";
import PickupNew from "./pickup-new";
import PickupBlockChooseType from "./pickup-choose-type";
import PhoneNumberLookup from "./phoneNumber-lookup";
import DonorChoice from "./donor-choose-account-or-guest";
import DonorLogin from "./donor-login";
import Tracking from "utils/tracking";
import DonationGuidelines from "components/DonationGuidelines";
import { withRouter } from "react-router";

/**
 * PickupFormComponent
 */
class PickupFormComponent extends React.Component {
  steps = [
    {
      title: `Where would you like us to pick up your donation?`,
      description: `Pickup Location`,
      render: () => {
        if (this.props.isGuest) return <PickupBlockSearchByGoogleAddress />;

        const { user } = this.props;
        const { addNewLocation } = this.props.publicPickupForm;

        if (user && user.locations && !addNewLocation) {
          return (
            <PickupBlockSearchByLocation
              useableLocations={user.locations.filter(
                (location) => !location.disabled
              )}
            />
          );
        }

        return (
          <PickupBlockSearchByGoogleAddress
            previousStep={
              addNewLocation && user
                ? () => {
                    this.props.resetFormData(["addNewLocation"]);
                  }
                : null
            }
          />
        );
      },
    },
    ...(this.props.infoPage !== ""
      ? [
          {
            title: `What to know before you donate`,
            description: ``,
            render: () => <InfoPage />,
          },
        ]
      : []),
    {
      title: `Do you plan on donating any large items or furniture?`,
      description: `Describe your donation`,
      render: () => <PickupBlockChooseType />,
    },
    {
      title: `Please choose the date that works best for you.`,
      description: `Choose a pickup date`,
      render: () => <PickupBlockSelect />,
    },
    ...(this.props.donationGuidelines !== "" && this.props.settingsLoaded
      ? [
          {
            title: "Agree to the donation guidelines",
            description: "Donation guidelines",
            render: () => (
              <DonationGuidelines
                previousStep={this.props.previousStep}
                nextStep={this.props.nextStep}
                navigation={this.props.publicPickupForm.navigation}
                isRequire={
                  this.props.publicPickupForm.requireAcceptDonationGuidelines
                }
              />
            ),
          },
        ]
      : []),

    ...(this.props.isGuest
      ? [
          {
            title: `What's your phone number? We'll need it to contact you on your pickup day.`,
            description: `Phone Number`,
            render: () => <PhoneNumberLookup />,
          },
          {
            title: `Donor information`,
            description: `Finalize pickup`,
            render: () => {
              const { isNewDonor, newDonorFormComplete, skipRegistration } =
                this.props.publicPickupForm;

              if (isNewDonor) {
                if (newDonorFormComplete) {
                  if (skipRegistration) {
                    Tracking.track("Step 5: Chose to skip Donor Registration");
                    this.props.nextStep();
                  }

                  return (
                    <DonorChoice
                      previousStep={() => {
                        this.props.resetFormData(["newDonorFormComplete"]);
                      }}
                    />
                  );
                } else {
                  return <DonorNew />;
                }
              } else {
                return <DonorLogin />;
              }
            },
          },
        ]
      : []),
    {
      title: `Complete your address details`,
      description: `Confirm Address`,
      render: () => (
        <LocationNew
          pickupLocation={this.props.publicPickupForm.location}
          user={this.props.user}
          currentStep={this.props.publicPickupForm.step}
        />
      ),
    },
    {
      title: "Upload images of your items",
      description: "Upload images",
      render: () => <ImageUpload />,
    },
    {
      title: `Confirm your pickup details`,
      description: `Finalize pickup`,
      render: () => (
        <PickupNew
          notes={this.props.notes}
          pickupLocation={this.props.publicPickupForm.location}
          pickupBlock={this.props.publicPickupForm.pickupBlock}
          user={this.props.user}
          hasFurniture={this.props.publicPickupForm.hasFurniture}
          onSubmitSuccess={
            this.props.isGuest
              ? () => this.props.nextStep()
              : this.props.formComplete
          }
          publicFormValues={this.props.publicPickupForm}
          previousStep={this.props.previousStep}
        />
      ),
    },
    ...(this.props.isGuest
      ? [
          {
            title: `Your pickup has been scheduled!`,
            description: `Finalize pickup`,
            render: () => {
              const { date } = this.props.publicPickupForm.pickupBlock;
              const { publicPickupForm } = this.props;
              // Redirect to a thank you page after a pickups is created, this is used for tracking purposes.
              // TODO: This could just as easily be reaplced by an event posted to the tracking service.
              if (process.env.REACT_APP_THANK_YOU_REDIRECT) {
                window.location.replace(
                  `${process.env.REACT_APP_THANK_YOU_REDIRECT}/?id=${publicPickupForm.user.id}&pn=${publicPickupForm.phoneNumber}&em=${publicPickupForm.email}&fn=${publicPickupForm.fname}&ln=${publicPickupForm.lname}&rid=${publicPickupForm.pickupBlock.regionId}`
                );
              }

              return (
                <div className={"box p-4"}>
                  <h4>
                    Thank you for your support, your pickup is confirmed for{" "}
                    {moment.utc(date).format("dddd, MMMM Do, YYYY")}.{" "}
                  </h4>
                  <p>
                    If you need to make changes, cancel or reschedule contact us
                    at{" "}
                    <a
                      href={`tel:${process.env.REACT_APP_CONTACT_PHONE_NUMBER_RAW}`}
                    >
                      {process.env.REACT_APP_CONTACT_PHONE_NUMBER}
                    </a>
                    .
                  </p>
                  <p>
                    {" "}
                    You will receive a reminder call the night before your
                    scheduled pickup date. Please check your email for important
                    information regarding our pickup guideline.
                  </p>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  renderStepProgress() {
    const { step, initialStep, isUsingInitialFlow } =
      this.props.publicPickupForm;

    const stepToUse = isUsingInitialFlow ? initialStep : step;
    let currentStep = this.steps[stepToUse - 1];
    let nextStep = this.steps[stepToUse];

    const { title } = currentStep;
    return (
      <Container>
        <div className={"pb-4"}>
          <div style={{ fontSize: "1.25em" }} className="pb-3 pt-3">
            {title}
          </div>
          <Progress
            color="secondary"
            value={(stepToUse / this.steps.length) * 100}
          />
          <Row>
            {/*<Col>*/}
            {/*  <small>*/}
            {/*    {stepToUse}/{this.steps.length}*/}
            {/*  </small>*/}
            {/*</Col>*/}
            <Col className={"text-right"}>
              <small>{nextStep ? `Next: ${nextStep.description}` : null}</small>
            </Col>
          </Row>
        </div>
      </Container>
    );
  }

  renderCurrentStep = () => {
    const { step, initialStep, isUsingInitialFlow } =
      this.props.publicPickupForm;

    return this.steps[(isUsingInitialFlow ? initialStep : step) - 1].render();
  };

  render() {
    return (
      <Switch>
        <Route path={`${this.props.match.path}/:step`}>
          {({ match }) => {
            const urlStep = Number(match.params.step);

            if (urlStep !== 1 && this.props.publicPickupForm.pristine)
              return <Redirect to={`${this.props.match.path}/1`} />;

            if (this.props.publicPickupForm.step < urlStep) {
              this.props.nextStep(false);
              return null;
            } else if (this.props.publicPickupForm.step > urlStep) {
              this.props.previousStep(false);
              return null;
            }

            return (
              <div>
                {this.renderStepProgress()}
                {this.renderCurrentStep()}
                <div style={{ padding: "1em", color: "#8a8a8a" }}>
                  {process.env.REACT_APP_CONTACT_PHONE_NUMBER}
                </div>
              </div>
            );
          }}
        </Route>
        <Redirect to={`${this.props.match.path}/1`} />
      </Switch>
    );
  }
}

export default withRouter(PickupFormComponent);

// Parent component holds state (step)
// Each step is a pure render component? Pass in FormValues setFormValues using dispatched methods passed in
// 1. Select Pickup Block
// 2. (SearSelect User/Add User
// 3. Select Location/ Add Location
// 4. Add Any
